// An in-app replacement for window.confirm.
//
// The browser's own dialog is drawn by Chrome, not by us: it carries the page's
// URL, ignores the app's styling and dark theme, and cannot say anything beyond
// one line of plain text — so "Delete entire Experience section?" arrived
// looking like a browser security prompt on the page a user is most anxious
// about. It is also synchronous: it blocks the main thread, so the click that
// opened it cannot render a pending state behind it.
//
// confirm() here returns a promise that resolves true or false, which is a drop
// in replacement at the call site:
//
//   if (!(await confirm({ title: '...', body: '...' }))) return;
//
// Passing cancelLabel: null drops the second button, which makes it an alert
// that the caller can await — the same dialog rather than a second component.
//
// The promise's resolver is held in a ref and settled by whichever button is
// pressed. Dismissing the dialog any other way — Escape, the backdrop, the
// close button — resolves false, so a caller can never be left awaiting a
// promise that no longer has a dialog attached to it.
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
};

const DEFAULTS = {
  title: 'Are you sure?',
  body: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  // 'danger' for anything that destroys work, which is most of these.
  variant: 'danger',
  icon: 'help',
};

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((answer) => {
    // Cleared before resolving: awaiting code may open another dialog
    // immediately, and it must not find this one's resolver still in place.
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);
    if (resolve) resolve(answer);
  }, []);

  const confirm = useCallback((opts = {}) => {
    // A second call while one is open answers the first as cancelled rather
    // than abandoning it — that promise would otherwise never settle.
    if (resolverRef.current) {
      const stale = resolverRef.current;
      resolverRef.current = null;
      stale(false);
    }
    setOptions({ ...DEFAULTS, ...(typeof opts === 'string' ? { body: opts } : opts) });
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        show={Boolean(options)}
        onHide={() => settle(false)}
        centered
        className="claude-theme"
        backdrop="static"
        keyboard
      >
        {options && (
          <>
            <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title className="fw-bold d-flex align-items-center" style={{ fontSize: '1.05rem' }}>
                <span
                  className={`material-symbols-rounded me-2 text-${options.variant}`}
                  style={{ fontSize: 'inherit', verticalAlign: 'middle' }}
                >
                  {options.icon}
                </span>
                {options.title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
              {options.body && <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{options.body}</p>}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
              {options.cancelLabel !== null && (
                <Button variant="light" onClick={() => settle(false)} className="rounded-pill px-3">
                  {options.cancelLabel}
                </Button>
              )}
              <Button
                variant={options.variant}
                onClick={() => settle(true)}
                className="rounded-pill px-3"
                autoFocus
              >
                {options.confirmLabel}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
};
